import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, Grid, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL, getAuthHeaders, getStoredUser } from '../auth';
import { ErrorBanner, LoadingPane, PageHeader } from '../components/page-elements';

const renderValue = (value, propKey = null, fallback = 'N/A') => {
  if (!value) return fallback;
  if (typeof value === 'object') {
    if (propKey && value[propKey]) return value[propKey];
    if (value.fullName) return value.fullName;
    if (value.firstName || value.lastName) return `${value.firstName || ''} ${value.lastName || ''}`.trim();
    return fallback;
  }
  return value;
};

const getBolStatusChipProps = (status) => {
  switch (status) {
    case 'Draft':
      return { color: 'warning', variant: 'filled' };
    case 'Completed':
      return { color: 'success', variant: 'filled' };
    default:
      return { color: 'default', variant: 'filled' };
  }
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return 'N/A';
  return dt.toLocaleString();
};
const formatAddress = (entity = {}, fields = []) => {
  const line1 = fields.map((f) => entity?.[f]).filter(Boolean).join(', ');
  return line1 || 'N/A';
};
const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const LIABILITY_ACKNOWLEDGMENT =
  'LIABILITY ACKNOWLEDGMENT: BY SIGNING ABOVE AND/OR ACCEPTING POSSESSION OF THE LOAD, DRIVER AND CARRIER ACKNOWLEDGE THAT THE MATERIALS HAVE BEEN LOADED BY THE RAILROAD IN APPARENT GOOD ORDER AND CONDITION, UNLESS OTHERWISE NOTED AT THE TIME OF LOADING. FROM THAT MOMENT FORWARD, DRIVER AND CARRIER ASSUME FULL AND EXCLUSIVE RESPONSIBILITY AND LIABILITY FOR THE CUSTODY, SECUREMENT, TRANSPORT, AND DELIVERY OF THE MATERIALS. THE RAILROAD SHALL HAVE NO LIABILITY FOR ANY LOSS, DAMAGE, CONTAMINATION, SPILLAGE, DELAY, OR CLAIM OF ANY KIND ARISING AFTER DRIVER ACCEPTS THE LOAD, REGARDLESS OF CAUSE. CARRIER AGREES TO INDEMNIFY, DEFEND, AND HOLD HARMLESS THE RAILROAD, ITS AFFILIATES, AND EMPLOYEES FROM ANY AND ALL SUCH CLAIMS, DAMAGES, OR EXPENSES.';

const DetailRow = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ py: 0.4 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={600} textAlign="right">
      {value === null || value === undefined || value === '' ? 'N/A' : value}
    </Typography>
  </Stack>
);

const BOLDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useMemo(() => getStoredUser(), []);
  const isInternal = user?.role === 'internal' || user?.role === 'admin';

  const [bol, setBol] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [emailing, setEmailing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [emailStatus, setEmailStatus] = useState({ type: '', message: '' });

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    setError('');
    fetch(`${API_BASE_URL}/api/bols/${id}`, {
      headers: getAuthHeaders(),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Failed to load BOL details');
        return response.json();
      })
      .then((data) => setBol(data))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <LoadingPane label="Loading BOL details..." />;

  if (!bol) {
    return (
      <Box p={2}>
        <ErrorBanner message={error || 'BOL not found'} />
        <Button variant="outlined" onClick={() => navigate('/bols')}>Back to BOLs</Button>
      </Box>
    );
  }

  const order = bol.orderNumber || {};
  const isSplitLoad = Boolean(bol.splitLoad && bol.secondaryRailcarID);
  const primaryGrossWeight = toFiniteNumber(bol.grossWeight);
  const primaryTareWeight = toFiniteNumber(bol.tareWeight);
  const primaryNetWeight = toFiniteNumber(bol.primaryNetWeight) ?? toFiniteNumber(bol.netWeight);
  const primaryTonWeight = toFiniteNumber(bol.primaryTonWeight)
    ?? (primaryNetWeight != null ? primaryNetWeight / 2000 : null);
  const secondaryGrossWeight = toFiniteNumber(bol.secondaryGrossWeight);
  const secondaryTareWeight = toFiniteNumber(bol.secondaryTareWeight);
  const secondaryNetWeight = toFiniteNumber(bol.secondaryNetWeight);
  const secondaryTonWeight = toFiniteNumber(bol.secondaryTonWeight);
  const totalGrossWeight =
    isSplitLoad && primaryGrossWeight != null && secondaryGrossWeight != null
      ? primaryGrossWeight + secondaryGrossWeight
      : primaryGrossWeight;
  const totalTareWeight =
    isSplitLoad && primaryTareWeight != null && secondaryTareWeight != null
      ? primaryTareWeight + secondaryTareWeight
      : primaryTareWeight;
  const totalNetWeight = toFiniteNumber(bol.netWeight);
  const totalTonWeight = toFiniteNumber(bol.tonWeight);

  const handlePrintBOL = () => {
    const customer = order.customerName || bol.customerName || {};
    const receiver = order.receiverName || {};
    const project = order.projectName || {};
    const primaryNetWeight = bol.primaryNetWeight ?? bol.netWeight;
    const primaryTonWeight = bol.primaryTonWeight ?? (primaryNetWeight != null ? Number(primaryNetWeight) / 2000 : null);

    const customerAddress = formatAddress(customer, [
      'customerAddress1',
      'customerAddress2',
      'customerCity',
      'customerState',
      'customerZip',
    ]);

    const receiverAddress = project.fullAddress || formatAddress(receiver, [
      'billingAddress1',
      'billingAddress2',
      'billingCity',
      'billingState',
      'billingZip',
    ]);

    const customerLogo = customer.customerLogo || '';

    const printContent = `
      <html>
        <head>
          <title>BOL ${order.orderNumber || 'N/A'}</title>
          <style>
            @page { size: auto; margin: 0.35in; }
            * { box-sizing: border-box; }
            body { font-family: Arial, sans-serif; margin: 0; color: #1f2937; font-size: 12px; line-height: 1.25; }
            .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; }
            .title-wrap h2 { margin: 0 0 2px 0; font-size: 18px; }
            .meta { font-size: 11px; color: #4b5563; margin: 1px 0; }
            .logo { max-height: 52px; max-width: 170px; object-fit: contain; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px; }
            .card { border: 1px solid #d1d5db; border-radius: 6px; padding: 6px 8px; }
            .card h3 { margin: 0 0 4px 0; font-size: 12px; color: #111827; }
            .card p { margin: 2px 0; font-size: 11px; }
            .signature-wrap { margin-top: 8px; border: 1px solid #d1d5db; border-radius: 6px; padding: 6px 8px; }
            .signature-wrap h3 { margin: 0 0 4px 0; font-size: 12px; color: #111827; }
            .signature-meta { font-size: 11px; margin-bottom: 4px; }
            .signature-image { max-width: 250px; max-height: 70px; border: 1px solid #d1d5db; border-radius: 4px; background: #fff; }
            .disclaimer { margin-top: 8px; padding: 6px 8px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 9px; line-height: 1.25; color: #374151; background: #f9fafb; }
            table { width: 100%; border-collapse: collapse; margin-top: 6px; }
            th, td { border: 1px solid #d1d5db; padding: 5px 6px; text-align: left; font-size: 11px; }
            th { background: #f9fafb; width: 30%; }
            .details-grid { display: grid; grid-template-columns: ${isSplitLoad ? '1fr 1fr' : '1fr'}; gap: 8px; margin-top: 6px; }
            .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 6px; }
            .split-col h3 { margin: 3px 0 0 0; font-size: 12px; color: #111827; }
            .totals-table { margin-top: 8px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title-wrap">
              <h2>BOL ${order.orderNumber || bol._id || 'N/A'}</h2>
              <div class="meta">Date: ${bol.bolDate ? new Date(bol.bolDate).toLocaleString() : 'N/A'}</div>
              <div class="meta">Status: ${bol.status || 'Draft'}</div>
            </div>
            ${customerLogo ? `<img class="logo" src="${customerLogo}" alt="Customer Logo" />` : ''}
          </div>

          <div class="grid">
            <div class="card">
              <h3>Customer</h3>
              <p><strong>Name:</strong> ${renderValue(customer, 'customerName')}</p>
              <p><strong>Address:</strong> ${customerAddress}</p>
              <p><strong>Origin:</strong> West Tennessee Railroad, 1061 James Buchanan Dr, Jackson, TN 38301</p>
            </div>
            <div class="card">
              <h3>Receiver / Delivery</h3>
              <p><strong>Receiver:</strong> ${renderValue(receiver, 'receiverName')}</p>
              <p><strong>Location:</strong> ${renderValue(project, 'projectName')}</p>
              <p><strong>Address:</strong> ${receiverAddress}</p>
            </div>
          </div>

          <div class="meta-grid">
            <table>
              <tr><th>Order Number</th><td>${order.orderNumber || 'N/A'}</td></tr>
              <tr><th>Material</th><td>${renderValue(order.materialName, 'materialName')}</td></tr>
            </table>
            <table>
              <tr><th>Split Load</th><td>${isSplitLoad ? 'Yes' : 'No'}</td></tr>
              <tr><th>Truck / Trailer</th><td>${bol.truckID || 'N/A'} / ${bol.trailerID || 'N/A'}</td></tr>
            </table>
          </div>

          <div class="details-grid">
            <div class="split-col">
              <h3>Primary Load</h3>
              <table>
                <tr><th>Railcar ID</th><td>${bol.railcarID || 'N/A'}</td></tr>
                <tr><th>Gross Weight</th><td>${bol.grossWeight || 'N/A'}</td></tr>
                <tr><th>Tare Weight</th><td>${bol.tareWeight || 'N/A'}</td></tr>
                <tr><th>Net Weight</th><td>${primaryNetWeight || 'N/A'}</td></tr>
                <tr><th>Ton Weight</th><td>${primaryTonWeight || 'N/A'}</td></tr>
              </table>
            </div>
            ${isSplitLoad ? `
              <div class="split-col">
                <h3>Secondary Load</h3>
                <table>
                  <tr><th>Railcar ID</th><td>${bol.secondaryRailcarID || 'N/A'}</td></tr>
                  <tr><th>Gross Weight</th><td>${bol.secondaryGrossWeight || 'N/A'}</td></tr>
                  <tr><th>Tare Weight</th><td>${bol.secondaryTareWeight || 'N/A'}</td></tr>
                  <tr><th>Net Weight</th><td>${bol.secondaryNetWeight || 'N/A'}</td></tr>
                  <tr><th>Ton Weight</th><td>${bol.secondaryTonWeight || 'N/A'}</td></tr>
                </table>
              </div>
            ` : ''}
          </div>

          <table class="totals-table">
            <tr><th>Total Net Weight</th><td>${bol.netWeight || 'N/A'}</td></tr>
            <tr><th>Total Ton Weight</th><td>${bol.tonWeight || 'N/A'}</td></tr>
          </table>

          <div class="signature-wrap">
            <h3>Driver Signature</h3>
            <div class="signature-meta"><strong>Driver:</strong> ${bol.driverName || 'N/A'}</div>
            <div class="signature-meta"><strong>Signed At:</strong> ${bol.signedAt ? new Date(bol.signedAt).toLocaleString() : 'N/A'}</div>
            ${bol.driverSignatureImage ? `<img class="signature-image" src="${bol.driverSignatureImage}" alt="Driver Signature" />` : '<div class="signature-meta">No signature on file</div>'}
          </div>

          <div class="disclaimer">${LIABILITY_ACKNOWLEDGMENT}</div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(printContent);
    printWindow.document.close();

    const triggerPrint = () => {
      printWindow.focus();
      printWindow.print();
      setTimeout(() => printWindow.close(), 300);
    };

    const waitForImages = () => {
      const images = Array.from(printWindow.document.images || []);
      if (images.length === 0) {
        triggerPrint();
        return;
      }

      let doneCount = 0;
      const done = () => {
        doneCount += 1;
        if (doneCount >= images.length) {
          triggerPrint();
        }
      };

      images.forEach((img) => {
        if (img.complete) {
          done();
          return;
        }
        img.onload = done;
        img.onerror = done;
      });

      // Fallback: do not block printing forever on browser-specific image behavior.
      setTimeout(() => {
        if (doneCount < images.length) {
          triggerPrint();
        }
      }, 1200);
    };

    if (printWindow.document.readyState === 'complete') {
      waitForImages();
    } else {
      printWindow.onload = waitForImages;
    }
  };

  const handleEmailBOL = async () => {
    if (!bol?._id) return;

    setEmailStatus({ type: '', message: '' });
    setEmailing(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/bols/${bol._id}/email`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({}),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.message || 'Failed to email BOL');
      }

      setEmailStatus({
        type: 'success',
        message: payload.message || 'BOL email sent successfully.',
      });
    } catch (err) {
      setEmailStatus({
        type: 'error',
        message: err.message || 'Failed to email BOL',
      });
    } finally {
      setEmailing(false);
    }
  };

  const handleDeleteDraftBOL = async () => {
    if (!bol?._id || bol.status !== 'Draft') return;
    const orderNumber = order.orderNumber || bol._id;
    const confirmed = window.confirm(`Delete draft BOL ${orderNumber}? This cannot be undone.`);
    if (!confirmed) return;

    setEmailStatus({ type: '', message: '' });
    setDeleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/bols/${bol._id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(true),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || 'Failed to delete draft BOL');
      }

      navigate('/bols');
    } catch (err) {
      setEmailStatus({
        type: 'error',
        message: err.message || 'Failed to delete draft BOL',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box p={2}>
      <PageHeader
        title={`BOL ${order.orderNumber || bol._id?.slice(-6) || ''}`.trim()}
        subtitle="Load ticket details and completion data."
        actions={[
          <Button key="back" variant="outlined" onClick={() => navigate('/bols')}>
            Back to BOLs
          </Button>,
          <Button key="print" variant="outlined" color="info" onClick={handlePrintBOL}>
            Print
          </Button>,
          isInternal && bol.status === 'Completed' ? (
            <Button
              key="email"
              variant="contained"
              color="primary"
              onClick={handleEmailBOL}
              disabled={emailing}
            >
              {emailing ? 'Emailing...' : 'Email BOL'}
            </Button>
          ) : null,
          isInternal && bol.status !== 'Completed' ? (
            <Button key="complete" variant="contained" color="success" onClick={() => navigate(`/bols/complete/${bol._id}`)}>
              Complete BOL
            </Button>
          ) : null,
          isInternal && bol.status === 'Draft' ? (
            <Button
              key="delete"
              variant="outlined"
              color="error"
              onClick={handleDeleteDraftBOL}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Draft BOL'}
            </Button>
          ) : null,
        ].filter(Boolean)}
      />
      <ErrorBanner message={error} />
      {emailStatus.message ? (
        <Alert severity={emailStatus.type === 'error' ? 'error' : 'success'} sx={{ mb: 1.5 }}>
          {emailStatus.message}
        </Alert>
      ) : null}

      <Grid container spacing={1.5}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.8 }}>Core Details</Typography>
              <DetailRow
                label="Status"
                value={<Chip size="small" label={bol.status || 'Draft'} {...getBolStatusChipProps(bol.status || 'Draft')} />}
              />
              <DetailRow label="Order Number" value={order.orderNumber || 'N/A'} />
              <DetailRow label="BOL Date" value={formatDateTime(bol.bolDate)} />
              <DetailRow label="Inventory Source" value={bol.inventorySource === 'ground' ? 'Ground Inventory' : 'Railcar'} />
              {bol.inventorySource === 'ground' ? <DetailRow label="Ground Lot" value={bol.groundInventoryLot?._id || bol.groundInventoryLot || 'N/A'} /> : null}
              <DetailRow label="Customer" value={renderValue(order.customerName, 'customerName')} />
              <DetailRow label="Created By" value={renderValue(bol.createdBy, null)} />
              <DetailRow label="Completed By" value={renderValue(bol.completedBy, null)} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.8 }}>Routing & Equipment</Typography>
              <DetailRow label="Railcar ID" value={bol.railcarID || 'N/A'} />
              {isSplitLoad ? <DetailRow label="Secondary Railcar" value={bol.secondaryRailcarID || 'N/A'} /> : null}
              <DetailRow label="Truck ID" value={bol.truckID || 'N/A'} />
              <DetailRow label="Trailer ID" value={bol.trailerID || 'N/A'} />
              <DetailRow label="Shipper" value={renderValue(order.shipperName, 'shipperName')} />
              <DetailRow label="Location" value={renderValue(order.projectName, 'projectName')} />
              <DetailRow label="Material" value={renderValue(order.materialName, 'materialName')} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.8 }}>Weights & Times</Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} md={isSplitLoad ? 4 : 6}>
                  <Box sx={{ border: '1px solid var(--ui-border)', borderRadius: 1.5, p: 1.2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.4 }}>Primary Load</Typography>
                    <DetailRow label="Railcar" value={bol.railcarID || 'N/A'} />
                    <DetailRow label="Gross Weight" value={primaryGrossWeight ?? 'N/A'} />
                    <DetailRow label="Tare Weight" value={primaryTareWeight ?? 'N/A'} />
                    <DetailRow label="Net Weight" value={primaryNetWeight ?? 'N/A'} />
                    <DetailRow label="Ton Weight" value={primaryTonWeight ?? 'N/A'} />
                  </Box>
                </Grid>
                {isSplitLoad ? (
                  <Grid item xs={12} md={4}>
                    <Box sx={{ border: '1px solid var(--ui-border)', borderRadius: 1.5, p: 1.2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.4 }}>Secondary Load</Typography>
                      <DetailRow label="Railcar" value={bol.secondaryRailcarID || 'N/A'} />
                      <DetailRow label="Gross Weight" value={secondaryGrossWeight ?? 'N/A'} />
                      <DetailRow label="Tare Weight" value={secondaryTareWeight ?? 'N/A'} />
                      <DetailRow label="Net Weight" value={secondaryNetWeight ?? 'N/A'} />
                      <DetailRow label="Ton Weight" value={secondaryTonWeight ?? 'N/A'} />
                    </Box>
                  </Grid>
                ) : null}
                <Grid item xs={12} md={isSplitLoad ? 4 : 6}>
                  <Box sx={{ border: '1px solid var(--ui-border)', borderRadius: 1.5, p: 1.2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.4 }}>
                      {isSplitLoad ? 'Load Totals' : 'Timing'}
                    </Typography>
                    {isSplitLoad ? (
                      <>
                        <DetailRow label="Total Gross Weight" value={totalGrossWeight ?? 'N/A'} />
                        <DetailRow label="Total Tare Weight" value={totalTareWeight ?? 'N/A'} />
                        <DetailRow label="Total Net Weight" value={totalNetWeight ?? 'N/A'} />
                        <DetailRow label="Total Ton Weight" value={totalTonWeight ?? 'N/A'} />
                      </>
                    ) : (
                      <>
                        <DetailRow label="Weigh In Time" value={formatDateTime(bol.weighInTime)} />
                        <DetailRow label="Weigh Out Time" value={formatDateTime(bol.weighOutTime)} />
                        <DetailRow label="Completed At" value={formatDateTime(bol.completedAt)} />
                        <DetailRow label="Driver" value={bol.driverName || 'N/A'} />
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>
              {isSplitLoad ? (
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ border: '1px solid var(--ui-border)', borderRadius: 1.5, p: 1.2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.4 }}>Timing & Driver</Typography>
                    <DetailRow label="Weigh In Time" value={formatDateTime(bol.weighInTime)} />
                    <DetailRow label="Weigh Out Time" value={formatDateTime(bol.weighOutTime)} />
                    <DetailRow label="Completed At" value={formatDateTime(bol.completedAt)} />
                    <DetailRow label="Driver" value={bol.driverName || 'N/A'} />
                  </Box>
                </Box>
              ) : null}
              <Box mt={1}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.3 }}>Comments</Typography>
                <Typography variant="body2">{bol.comments || 'No comments provided.'}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BOLDetails;
